import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AlertCircle } from 'lucide-react'
import type { AppDispatch, RootState } from '@/redux'
import { dashboardCreationActions } from '@/redux/slice/dashboardCreationSlice'
import {
  selectAllRegistryDomainsOrdered,
  selectDashboardDomainRegistryStatus,
} from '@/redux/slice/dashboardDomainRegistrySlice'
import { discoverFilterValues } from '@/services/DashboardFilterDiscoveryService'
import { proposePanelsThunk } from '@/services/DashboardProposedPanelsService'
import { flowTransitionThunk } from '@/services/DashboardFlowTransitionService'
import { buildFilterConfirmTemplates } from '@/services/transitionTemplates'
import { Button } from '@/components/ui/button'
import type {
  DashboardDomainRegistryItem,
  DashboardFilterDefinitionItem,
} from '@/types/DashboardDomainRegistry.types'
import type {
  DiscoveredFilterValue,
  FilterValueDiscoveryContext,
} from '@/types/DashboardFilterDiscovery.types'
import { DomainCard } from './FiltersBlock.primitives'
import { FilterRow } from './FilterRow'
import { partitionSharedAndPerDomainFilters } from './sharedFilters'
import type { SharedFilterEntry } from './sharedFilters'

/**
 * FiltersBlock — FSM gate: awaiting_filters
 *
 * Slice 4 of EPIC-132's metadata refactor (TDD
 * `plans/2026-05-08-slice-D-staff-with-domain-metadata-refactor-tdd.md` §7.7)
 * collapses the prior 4 hardcoded `hasDomain('X')` branches into a single
 * registry-driven render path. Per-filter UI is dispatched by `FilterRow` on
 * each filter's `uiHint`. Shared filters (e.g. `season` declared with
 * `sharedAcrossDomains: true`) render once at the top of the layout via
 * `partitionSharedAndPerDomainFilters`.
 *
 * Visual chrome (Tailwind classes, ARIA wiring, mockup-faithful copy) is
 * preserved verbatim from the prior implementation. No mockup change.
 *
 * Mockups (one per domain, per-state):
 *   - designs/dashboard-filter-discovery/2026-04-28-filter-enrollment-v1.html
 *   - designs/dashboard-filter-discovery/2026-04-28-filter-financial-v1.html
 *   - designs/dashboard-filter-discovery/2026-04-28-filter-bunks-v1.html
 *   Per-filter loading skeleton:
 *     - 2026-04-28-filter-enrollment-loading-v1.html
 *   Per-filter retryable error:
 *     - 2026-04-28-filter-enrollment-error-v1.html
 *   Blocking zero-seasons (no Confirm button):
 *     - 2026-04-28-filter-enrollment-zero-seasons-v1.html
 *
 * Persisted Redux state shape (`flow_context.domainFilterValues`) is
 * unchanged: keys remain `<pickerKey>.<filterCode>` so resume-after-reload
 * continues to work. Shared filter values are replicated across each selected
 * domain's per-pickerKey Redux entry at Confirm time so the BE flow_context
 * has accurate per-domain context.
 *
 * Brand guide: §12 (Filter Card — Component 5)
 */

// ─── Local state key conventions ──────────────────────────────────────────────
//
// Local UI state is keyed by `<pickerKey>.<filterCode>` for per-domain filters
// and `__shared__.<filterCode>` for shared filters. The shared key collapses
// the multi-domain shared-season case into a single source of truth that
// expands to per-pickerKey Redux entries at Confirm time.

const SHARED_KEY_PREFIX = '__shared__'

const sharedFilterKey = (filterCode: string): string =>
  `${SHARED_KEY_PREFIX}.${filterCode}`

const perDomainFilterKey = (pickerKey: string, filterCode: string): string =>
  `${pickerKey}.${filterCode}`

// ─── Helper types ─────────────────────────────────────────────────────────────

type FilterValueState = Record<string, string[] | string>

type DiscoveryChoiceMap = Record<string, DiscoveredFilterValue[] | null>

// ─── Multi-select selection helpers ───────────────────────────────────────────

const toggleInArray = (arr: string[], value: string): string[] =>
  arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]

// ─── Default-strategy resolver ────────────────────────────────────────────────

/**
 * Computes the initial value for a filter given its strategy and the
 * available discovery choices. Used both at mount (for static filters) and
 * on first discovery-ready (for discovery-driven filters with non-'none'
 * strategies). Returns an array for multi-cardinality, a string for single.
 */
function resolveInitialValueForFilter(
  filter: DashboardFilterDefinitionItem,
  discoveredChoices: DiscoveredFilterValue[] | null,
): string[] | string {
  const isMulti = filter.cardinality === 'multi'
  const empty: string[] | string = isMulti ? [] : ''

  switch (filter.defaultStrategy) {
    case 'current-season': {
      if (discoveredChoices === null || discoveredChoices.length === 0) {
        return empty
      }
      const current =
        discoveredChoices.find(c => c.isCurrent) ?? discoveredChoices[0]
      return isMulti ? [current.value] : current.value
    }
    case 'all-checked': {
      if (discoveredChoices === null) return empty
      const values = discoveredChoices.map(c => c.value)
      return isMulti ? values : values[0] ?? ''
    }
    case 'first-preset': {
      const choices = filter.staticChoices ?? []
      const display = filter.displayChoices
        ? choices.filter(c => filter.displayChoices!.includes(c.value))
        : choices
      const first = display[0]
      if (!first) return empty
      return isMulti ? [first.value] : first.value
    }
    case 'preset-subset': {
      const subset = filter.defaultPresetSubset ?? []
      return isMulti ? subset : subset[0] ?? ''
    }
    case 'none':
    default:
      return empty
  }
}

/**
 * True when a filter's current value matches its default-strategy result —
 * lets the FilterRow header display the green 'Default' badge.
 */
function isAtStrategyDefault(
  filter: DashboardFilterDefinitionItem,
  current: string[] | string,
  discoveredChoices: DiscoveredFilterValue[] | null,
): boolean {
  const init = resolveInitialValueForFilter(filter, discoveredChoices)
  if (Array.isArray(init) && Array.isArray(current)) {
    if (init.length === 0 && current.length === 0) return false
    if (init.length !== current.length) return false
    const sortedInit = [...init].sort()
    const sortedCurrent = [...current].sort()
    return sortedInit.every((v, i) => v === sortedCurrent[i])
  }
  if (typeof init === 'string' && typeof current === 'string') {
    return init !== '' && init === current
  }
  return false
}

// ─── Initialisation helper ────────────────────────────────────────────────────

function initialiseFilterValues(
  selectedDomains: DashboardDomainRegistryItem[],
  sharedFilters: SharedFilterEntry[],
  perDomainFilters: Map<string, DashboardFilterDefinitionItem[]>,
  persistedFilterValues: Record<string, unknown>,
): FilterValueState {
  const next: FilterValueState = {}

  const readPersistedAsArray = (key: string): string[] | undefined => {
    const v = persistedFilterValues[key]
    if (Array.isArray(v) && v.every(x => typeof x === 'string')) {
      return v as string[]
    }
    if (typeof v === 'string') return [v]
    return undefined
  }
  const readPersistedAsString = (key: string): string | undefined => {
    const v = persistedFilterValues[key]
    if (typeof v === 'string') return v
    if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'string') {
      return v[0] as string
    }
    return undefined
  }

  // Shared filters — key is `__shared__.<code>`. Hydrate from any persisted
  // per-pickerKey entry (the wire shape stores shared values under each
  // domain's pickerKey for backend per-domain context). Static filters with
  // no persisted state seed from defaultStrategy at mount.
  for (const entry of sharedFilters) {
    const code = entry.filter.code
    const key = sharedFilterKey(code)
    let hydrated: string[] | string | undefined
    if (entry.filter.cardinality === 'multi') {
      for (const d of selectedDomains) {
        const candidate = readPersistedAsArray(perDomainFilterKey(d.pickerKey, code))
        if (candidate !== undefined) {
          hydrated = candidate
          break
        }
      }
    } else {
      for (const d of selectedDomains) {
        const candidate = readPersistedAsString(perDomainFilterKey(d.pickerKey, code))
        if (candidate !== undefined) {
          hydrated = candidate
          break
        }
      }
    }
    if (hydrated !== undefined) {
      next[key] = hydrated
    } else if (!entry.filter.isDiscoveryDriven) {
      next[key] = resolveInitialValueForFilter(entry.filter, null)
    } else {
      next[key] = entry.filter.cardinality === 'multi' ? [] : ''
    }
  }

  // Per-domain filters — key is `<pickerKey>.<code>`. Hydrate from persisted
  // entry; static filters with no persisted entry seed from defaultStrategy.
  for (const domain of selectedDomains) {
    const filters = perDomainFilters.get(domain.pickerKey) ?? []
    for (const f of filters) {
      const key = perDomainFilterKey(domain.pickerKey, f.code)
      const hydrated =
        f.cardinality === 'multi'
          ? readPersistedAsArray(key)
          : readPersistedAsString(key)
      if (hydrated !== undefined) {
        next[key] = hydrated
      } else if (!f.isDiscoveryDriven) {
        next[key] = resolveInitialValueForFilter(f, null)
      } else {
        next[key] = f.cardinality === 'multi' ? [] : ''
      }
    }
  }

  return next
}

// ─── FiltersBlock ──────────────────────────────────────────────────────────────

export const FiltersBlock = () => {
  const dispatch: AppDispatch = useDispatch()
  const selectedDomainIds = useSelector(
    (state: RootState) => state.dashboardCreation.selectedDomainIds,
  )
  const credentialId = useSelector(
    (state: RootState) => state.dashboardCreation.credentialId,
  )
  const token = useSelector((state: RootState) => state.user.token)
  const dashboardId = useSelector(
    (state: RootState) => state.dashboardCreation.dashboardId,
  )

  const registryStatus = useSelector(selectDashboardDomainRegistryStatus)
  const allDomains = useSelector(selectAllRegistryDomainsOrdered)

  const persistedFilterValues = useSelector(
    (state: RootState) => state.dashboardCreation.domainFilterValues,
  )

  // Discovery state — entire choice/status maps. We slice these per filter at
  // render time (cheap; the map is small).
  const allDiscoveryChoices = useSelector(
    (state: RootState) => state.dashboardCreation.domainFilterChoices,
  )
  const allDiscoveryStatus = useSelector(
    (state: RootState) => state.dashboardCreation.domainFilterStatus,
  )

  // ── Resolve selected registry items, ordered by registry order. Filter out
  // any picker keys that have no registry entry (defensive — registry desync
  // would surface as a missing card, not a render crash).
  const selectedDomains = useMemo(
    () =>
      selectedDomainIds
        .map(id => allDomains.find(d => d.pickerKey === id))
        .filter(
          (d): d is DashboardDomainRegistryItem => d !== undefined,
        ),
    [selectedDomainIds, allDomains],
  )

  const { sharedFilters, perDomainFilters } = useMemo(
    () => partitionSharedAndPerDomainFilters(selectedDomains),
    [selectedDomains],
  )

  // ── Local filter values map. Keyed by `<pickerKey>.<code>` for per-domain
  // filters and `__shared__.<code>` for shared filters. We only initialise on
  // mount (and re-initialise when the selectedDomains identity changes) so
  // each per-pill-click stays as a single setState. Persisted Redux is
  // hydrated via `initialiseFilterValues` at first mount.
  const [filterValues, setFilterValues] = useState<FilterValueState>(() =>
    initialiseFilterValues(
      selectedDomains,
      sharedFilters,
      perDomainFilters,
      persistedFilterValues,
    ),
  )

  // Re-initialise when the selected domain set changes (rare; user can go
  // back and add/remove a domain). The deps are stable identity from useMemo.
  const lastSelectedKeyRef = useRef<string>(
    selectedDomains.map(d => d.pickerKey).join(''),
  )
  useEffect(() => {
    const nextKey = selectedDomains.map(d => d.pickerKey).join('')
    if (nextKey !== lastSelectedKeyRef.current) {
      lastSelectedKeyRef.current = nextKey
      setFilterValues(
        initialiseFilterValues(
          selectedDomains,
          sharedFilters,
          perDomainFilters,
          persistedFilterValues,
        ),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDomains, sharedFilters, perDomainFilters])

  // ── Track default-strategy applied state so we don't stomp on user choice
  // after the first auto-default. Keyed identically to filterValues.
  const defaultAppliedRef = useRef<Set<string>>(new Set())

  // ── Track whether the season filter is in a blocking-zero-seasons state.
  const [seasonBlocking, setSeasonBlocking] = useState(false)
  /**
   * Bug #3 (2026-05-14) — in-flight gating for the Confirm CTA.
   *   - true while flowTransitionThunk + proposePanelsThunk are running
   *   - prevents the triple-fire pattern that compounded the rate-limit
   *     pressure (each click fired a fresh /auth/apikey probe).
   *   - reset to false after both thunks settle OR after the user dismisses
   *     the inline transition-failure error so they can retry.
   */
  const [isConfirming, setIsConfirming] = useState(false)
  /**
   * Bug #3 (2026-05-14) — inline error shown when flowTransitionThunk fails
   * (network blip, 5xx, 409 FSM conflict). Pre-fix, the thunk silently
   * swallowed the failure and the FE marched on to proposePanelsThunk → the
   * backend session was still in `awaiting_filters` → 409 fsm_conflict → the
   * user saw the misleading "Please re-confirm your filters" message. Now we
   * stop, show a retry affordance, and never advance the FSM until the
   * transition lands.
   */
  const [flowTransitionError, setFlowTransitionError] =
    useState<{ status?: number; errorCode?: string } | null>(null)

  // ─── Discovery firing ──────────────────────────────────────────────────────
  //
  // For each shared filter declared `isDiscoveryDriven`, fire on mount + when
  // its parent context changes. For each per-domain filter declared
  // `isDiscoveryDriven`, fire on mount + when its parent context changes
  // (parent context is currently season for any session/bunk-scoped filter).

  const fireDiscovery = (
    qualifiedDomainId: string | null,
    filterCode: string,
    parentContext: FilterValueDiscoveryContext = {},
  ) => {
    if (!token || !credentialId || !qualifiedDomainId) return
    void dispatch(
      discoverFilterValues(
        token,
        qualifiedDomainId,
        filterCode,
        credentialId,
        parentContext,
      ),
    )
  }

  // Hoisted join key — react-hooks/exhaustive-deps cannot statically analyse
  // expressions in the dep array.
  const selectedDomainIdsKey = selectedDomainIds.join('')

  // The shared-season values drive cascading discovery for downstream
  // per-domain filters that depend on season. Hoist as an array for
  // deps + parentContext use below.
  const sharedSeasonValues = useMemo(() => {
    const v = filterValues[sharedFilterKey('season')]
    if (Array.isArray(v)) return v
    if (typeof v === 'string' && v !== '') return [v]
    return []
  }, [filterValues])
  const sharedSeasonKey = sharedSeasonValues.join('')

  // Initial discovery on mount + when selected domains change.
  useEffect(() => {
    if (!token || !credentialId) return
    // Shared filters fire discovery against their primaryQualifiedId.
    for (const entry of sharedFilters) {
      if (entry.filter.isDiscoveryDriven) {
        fireDiscovery(entry.primaryQualifiedId, entry.filter.code)
      }
    }
    // Per-domain filters that have NO declared parent dependency fire on mount.
    // (In v1 the only such per-domain isDiscoveryDriven filter is
    // `bunks.division`, which has no parent; `enrollment.session` and
    // `bunks.bunk` depend on season and fire in the cascading effect below.)
    for (const domain of selectedDomains) {
      const filters = perDomainFilters.get(domain.pickerKey) ?? []
      for (const f of filters) {
        if (!f.isDiscoveryDriven) continue
        if (filterCodeNeedsSeason(f.code)) continue
        fireDiscovery(domain.qualifiedDomainId, f.code)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, credentialId, selectedDomainIdsKey])

  // Cascading discovery for season-dependent per-domain filters.
  useEffect(() => {
    if (!token || !credentialId || sharedSeasonValues.length === 0) return
    for (const domain of selectedDomains) {
      const filters = perDomainFilters.get(domain.pickerKey) ?? []
      for (const f of filters) {
        if (!f.isDiscoveryDriven) continue
        if (!filterCodeNeedsSeason(f.code)) continue
        const parentContext: FilterValueDiscoveryContext = {
          season: sharedSeasonValues,
        }
        // bunk depends on division too (current SDK contract) — pass division
        // values when present.
        if (f.code === 'bunk') {
          const divisionValues = filterValues[
            perDomainFilterKey(domain.pickerKey, 'division')
          ]
          if (Array.isArray(divisionValues) && divisionValues.length > 0) {
            parentContext.division = divisionValues
          }
        }
        fireDiscovery(domain.qualifiedDomainId, f.code, parentContext)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, credentialId, sharedSeasonKey, selectedDomainIdsKey])

  // ─── Default-strategy resolver effect ───────────────────────────────────────
  // For each filter (shared + per-domain), apply its defaultStrategy once when
  // discovery first lands choices (or for static filters: on first observation
  // that no persisted value is present — handled in initialiseFilterValues).
  useEffect(() => {
    setFilterValues(prev => {
      let next = prev
      const apply = (
        filter: DashboardFilterDefinitionItem,
        key: string,
        choices: DiscoveredFilterValue[] | null,
      ) => {
        if (defaultAppliedRef.current.has(key)) return
        if (filter.defaultStrategy === undefined || filter.defaultStrategy === 'none') {
          defaultAppliedRef.current.add(key)
          return
        }
        // If user has already touched the value (non-empty array, non-empty
        // string), we don't stomp.
        const current = next[key]
        const isUserSet = Array.isArray(current)
          ? current.length > 0
          : typeof current === 'string' && current !== ''
        if (isUserSet) {
          defaultAppliedRef.current.add(key)
          return
        }
        // Discovery-driven filters wait for ready+choices; static fire on mount.
        if (filter.isDiscoveryDriven) {
          if (choices === null) return
          if (choices.length === 0) {
            // Empty choice array: nothing to default to; mark applied so we
            // don't keep re-evaluating after every render.
            defaultAppliedRef.current.add(key)
            return
          }
        }
        const initial = resolveInitialValueForFilter(filter, choices)
        if (next === prev) next = { ...prev }
        next[key] = initial
        defaultAppliedRef.current.add(key)
      }

      for (const entry of sharedFilters) {
        const key = sharedFilterKey(entry.filter.code)
        const stateKey = `${entry.primaryQualifiedId}.${entry.filter.code}`
        const choices = entry.filter.isDiscoveryDriven
          ? allDiscoveryStatus[stateKey] === 'ready'
            ? allDiscoveryChoices[stateKey] ?? null
            : null
          : null
        apply(entry.filter, key, choices)
      }
      for (const domain of selectedDomains) {
        const filters = perDomainFilters.get(domain.pickerKey) ?? []
        for (const f of filters) {
          const key = perDomainFilterKey(domain.pickerKey, f.code)
          const stateKey = `${domain.qualifiedDomainId}.${f.code}`
          const choices = f.isDiscoveryDriven
            ? allDiscoveryStatus[stateKey] === 'ready'
              ? allDiscoveryChoices[stateKey] ?? null
              : null
            : null
          apply(f, key, choices)
        }
      }
      return next
    })
  }, [
    sharedFilters,
    selectedDomains,
    perDomainFilters,
    allDiscoveryChoices,
    allDiscoveryStatus,
  ])

  // ─── Toggle / single-select handlers ────────────────────────────────────────

  const toggleMulti = (key: string, value: string) => {
    setFilterValues(prev => {
      const current = prev[key]
      const arr = Array.isArray(current)
        ? current
        : typeof current === 'string' && current !== ''
        ? [current]
        : []
      return { ...prev, [key]: toggleInArray(arr, value) }
    })
  }
  const setSingle = (key: string, value: string) => {
    setFilterValues(prev => ({ ...prev, [key]: value }))
  }

  // ─── Derived discovery choice maps for narration / default-badge ────────────
  // We build a small map per-filter-key of the most-recent ready choices.

  const choicesByKey: DiscoveryChoiceMap = useMemo(() => {
    const out: DiscoveryChoiceMap = {}
    for (const entry of sharedFilters) {
      const stateKey = `${entry.primaryQualifiedId}.${entry.filter.code}`
      out[sharedFilterKey(entry.filter.code)] =
        allDiscoveryStatus[stateKey] === 'ready'
          ? allDiscoveryChoices[stateKey] ?? null
          : null
    }
    for (const domain of selectedDomains) {
      const filters = perDomainFilters.get(domain.pickerKey) ?? []
      for (const f of filters) {
        const stateKey = `${domain.qualifiedDomainId}.${f.code}`
        out[perDomainFilterKey(domain.pickerKey, f.code)] =
          allDiscoveryStatus[stateKey] === 'ready'
            ? allDiscoveryChoices[stateKey] ?? null
            : null
      }
    }
    return out
  }, [
    sharedFilters,
    selectedDomains,
    perDomainFilters,
    allDiscoveryChoices,
    allDiscoveryStatus,
  ])

  // ─── Confirm CTA ────────────────────────────────────────────────────────────

  const handleConfirm = async () => {
    // Bug #3 (2026-05-14): gate the click so the triple-fire pattern that
    // compounded rate-limit pressure can't happen. Disabled-while-inflight
    // is enforced on the Button below; this is the belt-and-suspenders.
    if (isConfirming) return
    setIsConfirming(true)
    setFlowTransitionError(null)
    // Persist per-domain Redux entries. Shared filter values are replicated
    // across each selected domain's pickerKey so the BE flow_context retains
    // accurate per-domain context.
    const sharedEntries: Array<[string, string[] | string]> = sharedFilters.map(
      e => [e.filter.code, filterValues[sharedFilterKey(e.filter.code)] ?? []],
    )
    const domainFilterValues: Record<string, unknown> = {}
    for (const domain of selectedDomains) {
      for (const [code, value] of sharedEntries) {
        const key = perDomainFilterKey(domain.pickerKey, code)
        dispatch(
          dashboardCreationActions.setDomainFilterValue({ key, value }),
        )
        domainFilterValues[key] = value
      }
      const filters = perDomainFilters.get(domain.pickerKey) ?? []
      for (const f of filters) {
        const key = perDomainFilterKey(domain.pickerKey, f.code)
        const value = filterValues[key] ?? (f.cardinality === 'multi' ? [] : '')
        dispatch(
          dashboardCreationActions.setDomainFilterValue({ key, value }),
        )
        domainFilterValues[key] = value
      }
    }

    if (token && dashboardId) {
      // Build the human-readable domain label and per-filter summaries.
      const domainName =
        selectedDomains.map(d => d.displayName).join(' + ') ||
        selectedDomainIds
          .map(id => id.charAt(0).toUpperCase() + id.slice(1))
          .join(' + ') ||
        'Enrollment'

      const labelLookup = (
        choices: DiscoveredFilterValue[] | null,
        values: string[],
      ): string[] => {
        if (!choices) return values
        const map = new Map(choices.map(c => [c.value, c.displayName]))
        return values.map(v => map.get(v) ?? v)
      }

      const filterSummaries: Array<{ label: string; values: string[] }> = []

      for (const entry of sharedFilters) {
        const key = sharedFilterKey(entry.filter.code)
        const v = filterValues[key]
        const arr = Array.isArray(v) ? v : typeof v === 'string' && v !== '' ? [v] : []
        const labels = labelLookup(choicesByKey[key], arr)
        filterSummaries.push({
          label: entry.filter.displayName,
          values: labels.length > 0 ? labels : [`All ${entry.filter.displayName.toLowerCase()}`],
        })
      }

      for (const domain of selectedDomains) {
        const filters = perDomainFilters.get(domain.pickerKey) ?? []
        for (const f of filters) {
          const key = perDomainFilterKey(domain.pickerKey, f.code)
          const v = filterValues[key]
          const arr = Array.isArray(v) ? v : typeof v === 'string' && v !== '' ? [v] : []
          const labels = labelLookup(choicesByKey[key], arr)
          filterSummaries.push({
            label: f.displayName,
            values:
              labels.length > 0
                ? labels
                : [`All ${f.displayName.toLowerCase()}`],
          })
        }
      }

      const templates = buildFilterConfirmTemplates(domainName, filterSummaries)

      const transitionKey =
        globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
      // Bug #3 (2026-05-14): the thunk now returns FlowTransitionResult.
      // Gate the FE FSM advance + proposePanelsThunk on `ok === true` so we
      // never march on against a stale BE session — that mismatch was the
      // root cause of the "Please re-confirm your filters" message.
      const transitionResult = await dispatch(
        flowTransitionThunk(
          token,
          dashboardId,
          {
            toState: 'awaiting_panels',
            contextDelta: {
              selectedDomainIds,
              domainFilterValues,
            },
            ...templates,
          },
          transitionKey,
        ),
      )

      if (!transitionResult.ok) {
        setFlowTransitionError({
          status: transitionResult.status,
          errorCode: transitionResult.errorCode,
        })
        setIsConfirming(false)
        return
      }
    }

    // Advance the FE; PanelsProposedBlock paints its skeleton while
    // proposePanelsThunk is in flight. Dispatching `setFlowState('awaiting_panels')`
    // causes DashboardCreationShell to swap to PanelsProposedBlock and unmount
    // this component — so we intentionally do NOT await the thunk and do NOT
    // clear isConfirming here (the setter would target an unmounted component).
    // The thunk's progress is owned by PanelsProposedBlock's skeleton.
    dispatch(dashboardCreationActions.setFlowState('awaiting_panels'))

    if (token && dashboardId) {
      const idempotencyKey =
        globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
      void dispatch(proposePanelsThunk(token, dashboardId, idempotencyKey))
    }
  }

  // ─── Top-level render ──────────────────────────────────────────────────────

  if (registryStatus === 'error') {
    return (
      <div
        data-testid="filters-block"
        role="alert"
        className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-3.5 flex flex-col gap-2 max-w-[600px]"
      >
        <div className="flex items-center gap-2.5">
          <AlertCircle
            size={18}
            className="text-destructive flex-shrink-0"
            aria-hidden="true"
          />
          <span className="text-sm font-semibold text-primary">
            Couldn&apos;t load the dashboard catalog
          </span>
        </div>
        <p className="text-xs text-secondary leading-relaxed">
          We couldn&apos;t load the dashboard catalog. Refresh the page to try again.
        </p>
      </div>
    )
  }

  // The "primary" qualified id used for shared-filter discovery / Redux state
  // keys is the first selected domain's. Pre-Slice 4 the same convention drove
  // the season-discovery dispatch.
  const primaryDomain = selectedDomains[0] ?? null

  // Get values for a filter row from local state with the right shape.
  const valuesFor = (key: string): string[] => {
    const v = filterValues[key]
    if (Array.isArray(v)) return v
    if (typeof v === 'string' && v !== '') return [v]
    return []
  }

  // Quick lookup: for `bunks.bunk` retry / checkAgain we need access to the
  // bunks division values from the same domain.
  const buildSeasonContext = (): FilterValueDiscoveryContext => ({
    season: sharedSeasonValues,
  })

  // ─── Confirmable: at least one shared/per-domain "season" value present ──
  // Pre-refactor the rule was `seasonsSelected.length === 0` disables Confirm.
  // Post-refactor: if a shared `season` filter exists and is empty, disable.
  // Otherwise enable when there's at least one selected domain.
  const sharedSeasonEntry = sharedFilters.find(e => e.filter.code === 'season')
  const seasonRequiredAndEmpty =
    sharedSeasonEntry !== undefined && sharedSeasonValues.length === 0

  return (
    <div data-testid="filters-block" className="flex flex-col gap-3 w-full">
      {/* Shared filters card — rendered only when at least one shared filter
          exists. Visual treatment is the same DomainCard chrome — the
          mockups don't require a separate "Shared" treatment. */}
      {sharedFilters.length > 0 && primaryDomain && (
        <DomainCard ariaLabel="Shared filters">
          {sharedFilters.map(entry => {
            const key = sharedFilterKey(entry.filter.code)
            const choices = choicesByKey[key]
            const values = valuesFor(key)
            const showDefaultBadge = isAtStrategyDefault(
              entry.filter,
              filterValues[key] ?? (entry.filter.cardinality === 'multi' ? [] : ''),
              choices,
            )
            return (
              <div key={`shared.${entry.filter.code}`}>
                <FilterRow
                  filter={entry.filter}
                  qualifiedDomainId={entry.primaryQualifiedId}
                  domainKey={entry.primaryPickerKey}
                  selectedValues={values}
                  onToggle={value => toggleMulti(key, value)}
                  onSetSingle={value => setSingle(key, value)}
                  onBlockingChange={
                    entry.filter.code === 'season' ? setSeasonBlocking : undefined
                  }
                  onRetry={() =>
                    fireDiscovery(entry.primaryQualifiedId, entry.filter.code)
                  }
                  onCheckAgain={() =>
                    fireDiscovery(entry.primaryQualifiedId, entry.filter.code)
                  }
                  showDefaultBadge={showDefaultBadge}
                />
                {entry.filter.code === 'season' && values.length >= 2 && (
                  <p className="mt-1.5 text-xs text-accent">
                    ↗ {values.length} seasons selected — year-over-year panels
                    will be available
                  </p>
                )}
              </div>
            )
          })}
        </DomainCard>
      )}

      {/* Per-domain cards */}
      {!seasonBlocking &&
        selectedDomains.map(domain => {
          const filters = perDomainFilters.get(domain.pickerKey) ?? []
          if (filters.length === 0) return null
          return (
            <DomainCard
              key={domain.pickerKey}
              ariaLabel={`${domain.displayName} filters`}
            >
              {filters.map(f => {
                const key = perDomainFilterKey(domain.pickerKey, f.code)
                const choices = choicesByKey[key]
                const values = valuesFor(key)
                const showDefaultBadge = isAtStrategyDefault(
                  f,
                  filterValues[key] ?? (f.cardinality === 'multi' ? [] : ''),
                  choices,
                )
                // For filters that depend on season, hide row until season
                // is selected (preserves prior UX where session/bunk rows
                // didn't render until a season was chosen).
                if (
                  filterCodeNeedsSeason(f.code) &&
                  sharedSeasonValues.length === 0
                ) {
                  return null
                }
                return (
                  <FilterRow
                    key={f.code}
                    filter={f}
                    qualifiedDomainId={domain.qualifiedDomainId}
                    domainKey={domain.pickerKey}
                    selectedValues={values}
                    onToggle={value => toggleMulti(key, value)}
                    onSetSingle={value => setSingle(key, value)}
                    onRetry={() =>
                      fireDiscovery(
                        domain.qualifiedDomainId,
                        f.code,
                        filterCodeNeedsSeason(f.code) ? buildSeasonContext() : {},
                      )
                    }
                    onCheckAgain={() =>
                      fireDiscovery(
                        domain.qualifiedDomainId,
                        f.code,
                        filterCodeNeedsSeason(f.code) ? buildSeasonContext() : {},
                      )
                    }
                    showDefaultBadge={showDefaultBadge}
                  />
                )
              })}
            </DomainCard>
          )
        })}

      {/* Confirm CTA — hidden during the zero-seasons hard blocker per
          mockup `2026-04-28-filter-enrollment-zero-seasons-v1.html`. */}
      {!seasonBlocking && (
        <div className="max-w-[660px] mt-1 flex flex-col gap-2">
          {/* Bug #3 (2026-05-14): inline flow-transition error. Pre-fix the
              FE swallowed this failure and marched on, producing the
              misleading "Please re-confirm your filters" message later. */}
          {flowTransitionError && (
            <div
              role="alert"
              className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-3 flex items-start gap-2.5"
              data-testid="filters-block-transition-error"
            >
              <AlertCircle
                size={16}
                className="text-destructive flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div className="flex-1">
                <p className="text-sm font-semibold text-primary mb-0.5">
                  Couldn&apos;t save your filters
                </p>
                <p className="text-xs text-secondary leading-snug">
                  We hit a hiccup advancing the conversation. Click Confirm
                  again to retry — your selections are still here.
                </p>
              </div>
            </div>
          )}
          <Button
            type="button"
            className="w-full font-semibold"
            aria-label={
              isConfirming
                ? 'Confirming filters'
                : 'Confirm filters and proceed to panel proposals'
            }
            onClick={handleConfirm}
            disabled={
              seasonRequiredAndEmpty ||
              selectedDomains.length === 0 ||
              isConfirming
            }
          >
            {isConfirming ? 'Confirming…' : 'Confirm Filters — Propose Panels'}
          </Button>
        </div>
      )}
    </div>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * v1 cascading-discovery dependency table. Encoded inline for now — when the
 * SDK's filter definitions add an explicit `parentContext` declaration this
 * helper goes away. (Tracked separately; not in scope for Slice 4.)
 */
function filterCodeNeedsSeason(filterCode: string): boolean {
  return filterCode === 'session' || filterCode === 'bunk'
}
